/**
 * Common Zod schemas shared across all contracts
 *
 * DATABASE-FIRST: All schemas match PostgreSQL exactly
 * Source of truth: MCP postgres queries on 2026-01-02
 */
import { z } from 'zod';
// =============================================================================
// PRIMITIVE TRANSFORMERS
// PostgreSQL types that need JSON-safe representation
// =============================================================================
/**
 * BigInt as string
 * DB: bigint → JSON: string (JavaScript can't handle 64-bit integers)
 */
export const bigintString = z.string().regex(/^\d+$/, 'Must be a numeric string');
/**
 * Numeric/Decimal as string
 * DB: numeric → JSON: string (preserves precision)
 */
export const numericString = z.string().regex(/^-?\d+(\.\d+)?$/, 'Must be a decimal string').nullable();
/**
 * Timestamp as ISO string
 * DB: timestamp/timestamptz → JSON: ISO 8601 string
 */
export const timestampString = z.string().datetime().nullable();
/**
 * Timestamp that's NOT NULL
 */
export const timestampStringRequired = z.string().datetime();
// =============================================================================
// API RESPONSE WRAPPER
// =============================================================================
/**
 * Standard API response wrapper
 * All endpoints return this shape
 */
export const apiResponseSchema = (dataSchema) => z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.string().optional(),
});
/**
 * Success response (success: true, data required)
 */
export const successResponseSchema = (dataSchema) => z.object({
    success: z.literal(true),
    data: dataSchema,
});
/**
 * Error response (success: false, error required)
 */
export const errorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    data: z.record(z.unknown()).optional(),
});
// =============================================================================
// DEPOSIT STATUS
// Source: CHECK constraint 'valid_deposit_status' on deposits table
// =============================================================================
export const depositStatusValues = ['pending', 'paid', 'completed', 'failed', 'cancelled'];
export const depositStatusSchema = z.enum(depositStatusValues);
// =============================================================================
// WITHDRAWAL STATUS
// Source: CHECK constraint 'valid_withdrawal_status' on withdrawals table
// =============================================================================
export const withdrawalStatusValues = ['pending', 'paid', 'completed', 'declined'];
export const withdrawalStatusSchema = z.enum(withdrawalStatusValues);
// =============================================================================
// DEPOSIT LOG TYPES
// Source: CHECK constraint 'valid_deposit_log_type' on deposit_logs table
// =============================================================================
export const depositLogTypeValues = [
    'cascade',
    'webhook',
    'credited',
    'paid',
    'receipt_uploaded',
    'force_complete',
    'edit',
    'admin_edit',
    'cancelled',
    'failed',
];
export const depositLogTypeSchema = z.enum(depositLogTypeValues);
// =============================================================================
// WITHDRAWAL LOG TYPES
// Source: CHECK constraint 'valid_withdrawal_log_type' on withdrawal_logs table
// =============================================================================
export const withdrawalLogTypeValues = [
    'assignment',
    'cascade',
    'webhook',
    'manual',
    'edit',
    'decline',
    'complete',
    'admin_edit',
    'resend',
];
export const withdrawalLogTypeSchema = z.enum(withdrawalLogTypeValues);
// =============================================================================
// WEBHOOK STATUS (normalized from providers)
// Used in payment-service → backend communication
//
// NOTE: Providers send 'completed', which backend translates to deposit 'paid'.
//       'paid' is NOT a webhook status - it's a deposit/withdrawal internal status.
// =============================================================================
export const webhookStatusValues = ['pending', 'completed', 'failed', 'cancelled'];
export const webhookStatusSchema = z.enum(webhookStatusValues);
// =============================================================================
// REFERENCE NUMBER FORMATS
// =============================================================================
/**
 * Deposit reference number: DEP-YYYYMMDD-NNNNN
 */
export const depositReferenceSchema = z.string().regex(/^DEP-\d{8}-\d{5}$/, 'Must be in format DEP-YYYYMMDD-NNNNN');
/**
 * Withdrawal reference number: WTH-YYYYMMDD-NNNNN
 */
export const withdrawalReferenceSchema = z.string().regex(/^WTH-\d{8}-\d{5}$/, 'Must be in format WTH-YYYYMMDD-NNNNN');
/**
 * Referee withdrawal reference: REF-YYYYMMDD-NNNNN
 */
export const refereeReferenceSchema = z.string().regex(/^REF-\d{8}-\d{5}$/, 'Must be in format REF-YYYYMMDD-NNNNN');
/**
 * Any transaction reference
 */
export const transactionReferenceSchema = z.string().regex(/^(DEP|WTH|REF)-\d{8}-\d{5}$/, 'Must be in format XXX-YYYYMMDD-NNNNN');
// =============================================================================
// CARD NUMBER
// =============================================================================
/**
 * 16-digit card number (Luhn validation done in application layer)
 */
export const cardNumberSchema = z.string().regex(/^\d{16}$/, 'Must be 16 digits');
//# sourceMappingURL=common.js.map